<template>
  <b-modal
    :visible="visible"
    size="xl"
    hide-header
    hide-footer
    centered
    body-class="p-0"
    @change="$emit('update:visible', $event)"
    @hidden="onHidden"
  >
    <div class="assv-modal" v-if="slipId">
      <div class="assv-head">
        <div class="assv-head-left">
          <div class="assv-title">
            Mã phiếu kho:
            <span class="assv-code">{{ stockSlip.code || '—' }}</span>
          </div>
          <span v-if="typeBadgeLabel" class="assv-type-badge">{{ typeBadgeLabel }}</span>
          <div class="assv-sub">{{ detailSubtitle }}</div>
        </div>
        <button type="button" class="assv-close" @click="close">
          <i class="mdi mdi-close"></i>
        </button>
      </div>

      <div class="assv-body">
        <div v-if="loading" class="text-center text-muted py-5">
          <b-spinner small />
          Đang tải...
        </div>

        <template v-else>
          <div class="assv-cards">
            <div class="assv-card">
              <div class="assv-card-label">
                <i class="mdi mdi-file-document-outline"></i>
                BILL ID (ĐƠN HÀNG)
              </div>
              <div class="assv-card-value">{{ stockSlip.billId || '—' }}</div>
            </div>
            <div class="assv-card">
              <div class="assv-card-label">
                <i class="mdi mdi-warehouse"></i>
                KHO
              </div>
              <div class="assv-card-value">{{ stockSlip.storeName || '—' }}</div>
              <div v-if="stockSlip.storeId" class="assv-card-sub">Store ID: {{ stockSlip.storeId }}</div>
            </div>
            <div class="assv-card">
              <div class="assv-card-label">
                <i class="mdi mdi-package-variant-closed"></i>
                TỔNG TIỀN
              </div>
              <div class="assv-card-value">{{ convertPrice(grandTotal) }}</div>
              <div class="assv-card-sub">{{ details.length }} dòng · SL {{ totalQuantity }}</div>
            </div>
          </div>

          <div v-if="hasNotes" class="assv-notes">
            <div class="assv-notes-icon">
              <i class="mdi mdi-notebook-outline"></i>
            </div>
            <div class="assv-notes-content">
              <div v-if="showCustomerNote" class="assv-note-line">
                <span class="assv-note-label">Ghi chú khách hàng:</span>
                {{ formatNote(stockSlip.customerNote) }}
              </div>
              <p v-if="showCustomerNote && (showSaleNote || showWarrantyNote)" class="assv-note-divider" />
              <div v-if="showSaleNote" class="assv-note-line">
                <span class="assv-note-label">Ghi chú nội bộ:</span>
                {{ formatNote(stockSlip.saleNote || stockSlip.internalNote || stockSlip.description) }}
              </div>
              <p v-if="showSaleNote && showWarrantyNote" class="assv-note-divider" />
              <div v-if="showWarrantyNote" class="assv-note-line">
                <span class="assv-note-label">Ghi chú bảo hành:</span>
                {{ formatNote(stockSlip.warrantyNote) }}
              </div>
            </div>
          </div>

          <div class="assv-products">
            <div class="assv-products-head">
              <span class="assv-products-title">Danh sách sản phẩm</span>
              <span class="assv-products-count">{{ details.length }} dòng · Tổng SL {{ totalQuantity }}</span>
            </div>

            <table class="assv-table">
              <thead>
                <tr>
                  <th class="assv-col-index">#</th>
                  <th>SẢN PHẨM</th>
                  <th class="assv-col-imei">IMEI</th>
                  <th class="text-center assv-col-qty">SL</th>
                  <th class="text-right assv-col-price">ĐƠN GIÁ</th>
                  <th class="text-right assv-col-price">THÀNH TIỀN</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in details" :key="item.id || index">
                  <td class="text-center">{{ index + 1 }}</td>
                  <td>
                    <div class="assv-product">
                      <ProductImage
                        v-if="item.productImageUrl"
                        :src="item.productImageUrl"
                        class="assv-img"
                      />
                      <div v-else class="assv-img-placeholder">
                        <i class="mdi mdi-image-off-outline"></i>
                      </div>
                      <div>
                        <div class="assv-product-code">
                          {{ item.barCode || item.productBarCode || '—' }}
                          <span v-if="item.productCode">({{ item.productCode }})</span>
                        </div>
                        <div class="assv-product-name">{{ item.productName || '—' }}</div>
                        <div
                          v-if="item.inStockQuantity != null"
                          class="assv-product-stock"
                        >
                          Tồn: {{ convertPrice(item.inStockQuantity) }}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div v-if="formatLineImeiList(item).length" class="assv-imei-list">
                      <span
                        v-for="(imei, imeiIndex) in formatLineImeiList(item)"
                        :key="imeiIndex"
                        class="assv-imei"
                      >{{ imei }}</span>
                    </div>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td class="text-center">{{ convertPrice(item.quantity) }}</td>
                  <td class="text-right">{{ convertPrice(lineUnitPrice(item)) }}</td>
                  <td class="text-right font-weight-bold">{{ convertPrice(lineTotalAmount(item)) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="5" class="text-right assv-total-label">Tổng tiền</td>
                  <td class="text-right assv-total-value">{{ convertPrice(grandTotal) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </template>
      </div>

      <div class="assv-foot">
        <b-button variant="light" size="sm" @click="close">Đóng</b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import ApiService from '@/core/services/api.service'
import { convertPrice } from '@/utils/common'
import { STOCK_SLIP_TYPE } from '@/utils/enum'
import ProductImage from '@/view/components/products/ProductImage.vue'
import {
  getAutoStockSlipDetailSubtitle,
  getAutoStockSlipTypeBadgeLabel,
} from '@/utils/stockSlipOrigin'

export default {
  name: 'AutoStockSlipViewModal',
  components: { ProductImage },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    slipId: {
      type: [Number, String],
      default: null,
    },
    slipType: {
      type: [Number, String],
      default: null,
    },
    direction: {
      type: String,
      default: 'export',
      validator: (value) => ['export', 'import'].includes(value),
    },
  },
  data() {
    return {
      loading: false,
      stockSlip: {},
      details: [],
    }
  },
  computed: {
    typeBadgeLabel() {
      return getAutoStockSlipTypeBadgeLabel(this.stockSlip, this.direction)
    },
    detailSubtitle() {
      return getAutoStockSlipDetailSubtitle(this.stockSlip.id || this.slipId, this.direction)
    },
    totalQuantity() {
      return this.details.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
    },
    grandTotal() {
      if (this.stockSlip.totalAmount != null && this.stockSlip.totalAmount !== '') {
        return Number(this.stockSlip.totalAmount) || 0
      }
      return this.details.reduce((sum, item) => sum + this.lineTotalAmount(item), 0)
    },
    showCustomerNote() {
      return this.hasNoteValue(this.stockSlip.customerNote)
    },
    showSaleNote() {
      return this.hasNoteValue(
        this.stockSlip.saleNote || this.stockSlip.internalNote || this.stockSlip.description,
      )
    },
    showWarrantyNote() {
      return this.hasNoteValue(this.stockSlip.warrantyNote)
    },
    hasNotes() {
      return this.showCustomerNote || this.showSaleNote || this.showWarrantyNote
    },
  },
  watch: {
    visible(value) {
      if (value && this.slipId) {
        this.fetchDetail()
      }
    },
    slipId(value) {
      if (this.visible && value) {
        this.fetchDetail()
      }
    },
  },
  methods: {
    convertPrice,
    close() {
      this.$emit('update:visible', false)
    },
    onHidden() {
      this.stockSlip = {}
      this.details = []
    },
    hasNoteValue(value) {
      return value != null && value !== '' && value !== 'null'
    },
    formatNote(value) {
      if (!this.hasNoteValue(value)) return '—'
      return value
    },
    lineUnitPrice(item) {
      return item.unitPrice ?? item.afterVatPrice ?? item.productPriceMask ?? 0
    },
    lineTotalAmount(item) {
      if (item.totalAmount != null && item.totalAmount !== '') {
        return Number(item.totalAmount) || 0
      }
      const qty = Number(item.quantity) || 0
      return qty * (Number(this.lineUnitPrice(item)) || 0)
    },
    formatLineImeiList(item) {
      const raw = item.productImei || item.IMEI || item.imeiCode || ''
      if (!raw || !String(raw).trim()) return []
      return String(raw)
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean)
    },
    resolveDetailEndpoint() {
      const type = Number(this.stockSlip.type || this.slipType)
      if (type === STOCK_SLIP_TYPE.EXPORT_WARRANTY_FROM_BILL) {
        return 'v1/warranty-stock-slips'
      }
      return 'v1/stock-slips'
    },
    async fetchDetail() {
      this.loading = true
      this.stockSlip = { id: this.slipId, type: this.slipType }
      this.details = []
      try {
        const endpoint = this.resolveDetailEndpoint()
        const { data } = await ApiService.get(`${endpoint}/${this.slipId}`)
        const payload = data.data || {}
        this.stockSlip = payload
        this.details = payload.details || []
      } catch (error) {
        const message = error?.response?.data?.message || 'Không tải được chi tiết phiếu'
        this.$bvToast.toast(message, { title: 'Thông báo', variant: 'danger', solid: true })
        this.close()
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$assv-ink: #181c32;
$assv-muted: #7e8299;
$assv-faint: #b5b5c3;
$assv-border: #e4e6ef;
$assv-border-soft: #eef0f6;
$assv-surface-soft: #f8f9fc;
$assv-link: #3699ff;
$assv-radius-sm: 0.5rem;

.assv-modal {
  .assv-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 1.25rem 1.5rem 1rem;
    border-bottom: 1px solid $assv-border-soft;
  }

  .assv-head-left {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
  }

  .assv-title {
    font-size: 1rem;
    font-weight: 600;
    color: $assv-ink;
  }

  .assv-code {
    color: $assv-link;
    font-weight: 700;
  }

  .assv-type-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.2rem 0.65rem;
    border-radius: 999px;
    background: #eef6ff;
    color: $assv-link;
    font-size: 12px;
    font-weight: 600;
  }

  .assv-sub {
    flex-basis: 100%;
    font-size: 12px;
    color: $assv-muted;
  }

  .assv-close {
    border: 0;
    background: transparent;
    color: $assv-faint;
    font-size: 20px;
    line-height: 1;
    cursor: pointer;

    &:hover {
      color: $assv-ink;
    }
  }

  .assv-body {
    padding: 1.25rem 1.5rem;
  }

  .assv-cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.85rem;
    margin-bottom: 1rem;
  }

  .assv-card {
    border: 1px solid $assv-border-soft;
    border-radius: $assv-radius-sm;
    padding: 0.75rem 0.9rem;
  }

  .assv-card-label {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 600;
    color: $assv-muted;
    text-transform: uppercase;

    i {
      font-size: 14px;
    }
  }

  .assv-card-value {
    font-size: 15px;
    font-weight: 700;
    color: $assv-ink;
    margin-top: 4px;
  }

  .assv-card-sub {
    font-size: 12px;
    color: $assv-muted;
    margin-top: 2px;
  }

  .assv-notes {
    display: flex;
    gap: 12px;
    background: #fff8ec;
    border: 1px solid #ffe2b8;
    border-radius: $assv-radius-sm;
    padding: 0.85rem 1rem;
    margin-bottom: 1.1rem;
  }

  .assv-notes-icon {
    color: #d9a441;
    font-size: 20px;
    line-height: 1;
    padding-top: 2px;
  }

  .assv-note-line {
    font-size: 13px;
    color: $assv-ink;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .assv-note-label {
    font-weight: 600;
  }

  .assv-note-divider {
    border-top: 1px dashed #f0d7a8;
    margin: 0.55rem 0;
  }

  .assv-products-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.6rem;
  }

  .assv-products-title {
    font-size: 14px;
    font-weight: 700;
    color: $assv-ink;
  }

  .assv-products-count {
    font-size: 12px;
    color: $assv-muted;
  }

  .assv-table {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid $assv-border-soft;

    thead th {
      background: $assv-surface-soft;
      color: $assv-muted;
      font-size: 11px;
      font-weight: 700;
      padding: 0.6rem 0.85rem;
      text-align: left;
      border-bottom: 1px solid $assv-border-soft;
    }

    tbody td {
      padding: 0.7rem 0.85rem;
      font-size: 13px;
      color: $assv-ink;
      border-top: 1px solid $assv-border-soft;
      vertical-align: middle;
    }

    tfoot td {
      padding: 0.7rem 0.85rem;
      border-top: 1px solid $assv-border;
      background: $assv-surface-soft;
    }

    .text-right {
      text-align: right;
    }

    .text-center {
      text-align: center;
    }
  }

  .assv-col-index {
    width: 42px;
  }

  .assv-col-qty {
    width: 72px;
  }

  .assv-col-imei {
    width: 160px;
  }

  .assv-imei-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .assv-imei {
    display: inline-block;
    font-size: 12px;
    font-weight: 600;
    color: $assv-ink;
    background: $assv-surface-soft;
    border: 1px solid $assv-border-soft;
    border-radius: 6px;
    padding: 3px 8px;
    word-break: break-all;
  }

  .assv-col-price {
    width: 120px;
  }

  .assv-total-label {
    font-weight: 600;
    color: $assv-muted;
  }

  .assv-total-value {
    font-weight: 700;
    color: $assv-ink;
  }

  .assv-product {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .assv-img,
  .assv-img img {
    width: 42px;
    height: 42px;
    border-radius: $assv-radius-sm;
    object-fit: cover;
  }

  .assv-img-placeholder {
    width: 42px;
    height: 42px;
    border-radius: $assv-radius-sm;
    background: $assv-surface-soft;
    border: 1px solid $assv-border-soft;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $assv-faint;
    font-size: 18px;
  }

  .assv-product-code {
    font-size: 12px;
    color: $assv-muted;
    font-weight: 600;
  }

  .assv-product-name {
    font-size: 13px;
    font-weight: 600;
    color: $assv-ink;
  }

  .assv-product-stock {
    font-size: 12px;
    color: $assv-muted;
    margin-top: 2px;
  }

  .assv-foot {
    display: flex;
    justify-content: flex-end;
    padding: 1rem 1.5rem;
    border-top: 1px solid $assv-border-soft;
  }
}

@media (max-width: 991.98px) {
  .assv-modal .assv-cards {
    grid-template-columns: 1fr;
  }
}
</style>
